import { keys, includes, forOwn, forEach } from 'lodash';
import { ObjectSchema, mixed, string } from 'yup';
import 'yup-phone-lite';
import i18n from '@components/i18n';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ValidationSchema = ObjectSchema<any>;
type yupMultipleFilesOptions = {
  required?: boolean;
  max?: number;
};

const fileSize = 5_000_000; // 5MB
const supportedFormats = ['image/jpg', 'image/jpeg', 'image/png', 'application/pdf'];
const DEFAULT_MAX_FILES = 10;

export const yupFile = () => {
  return mixed()
    .test('fileSize', i18n.t('validation:fileSizeOutOfRange'), (file: File) => !file || (file && file.size < fileSize))
    .test(
      'fileFormat',
      i18n.t('validation:invalidFormat'),
      (file: File) => !file || (file && includes(supportedFormats, file.type)),
    );
};

export const yupMultipleFiles = ({ required = false, max = DEFAULT_MAX_FILES }: yupMultipleFilesOptions = {}) => {
  let schema = mixed<File[]>().default([]);

  if (required) {
    schema = schema.test('required', i18n.t('validation:required'), (files: File[]) => files && files.length > 0);
  }

  if (max) {
    schema = schema.test('max', i18n.t('validation:limitOutOfRange', { max }), (files: File[]) => files.length <= max);
  }

  return schema
    .test('fileFormat', i18n.t('validation:invalidFormat'), (files: File[]) => {
      forEach(files, (file) => {
        if (!supportedFormats.includes(file.type)) {
          return false;
        }
      });
      return true;
    })
    .test('fileSize', i18n.t('validation:fileSizeOutOfRange'), (files: File[]) => {
      forEach(files, (file) => {
        if (file.size > fileSize) return false;
      });
      return true;
    });
};

export const yupCsvFile = () => {
  return mixed()
    .test('fileSize', i18n.t('validation:fileSizeOutOfRange'), (file: File) => !file || (file && file.size < fileSize))
    .test(
      'fileFormat',
      i18n.t('validation:invalidFormat'),
      (file: File) => !file || (file && includes(['text/csv'], file.type)),
    );
};

export const yupMobileNumber = () => {
  return string()
    .required(i18n.t('validation:required'))
    .phone('KH', i18n.t('validation:validPhone', { countryName: i18n.t('common:cambodia') }));
}

/* eslint-disable */
export const getFields = (schema: ValidationSchema) => {
  return schema?.describe()?.fields;
};

export const getFieldKeys = (schema: ValidationSchema) => {
  return keys(getFields(schema));
};
/* eslint-enable */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isRequired = <T extends object>(schema: ValidationSchema) => {
  const result = {} as T;
  forOwn(schema.fields, (field, key) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    result[key as keyof T] = !!field?.exclusiveTests?.required as T[keyof T];
  });
  return result;
};
